import type { Device } from "@models";
import {
  CheckRounded,
  ContentCopyRounded,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { ListSubheader, MenuItem } from "@mui/material";
import type { Editor } from "@tiptap/react";
import { DocumentScopeChip } from "src/components/Documents/DocumentScopeChip";
import GenericButton from "src/components/GenericButton/GenericButton";
import { useTiptapCopyAction } from "src/components/TiptapEditor/useTiptapCopyAction";
import { CompactSelect } from "src/components/ui/CompactSelect";

type FrameViewerTopBarProps = {
  deviceId?: string;
  devices: Device[];
  editor: Editor | null;
  onDeviceSelect: (deviceId: string) => void;
  onShowAllConditionalsChange: (showAll: boolean) => void;
  scopeLabel: string | null;
  showAllConditionals: boolean;
};

/**
 * Frame-viewer counterpart of `TiptapDocViewerTopBar`, mirroring its layout
 * (left: context dropdown + scope chip, right: actions) with only the pieces
 * that make sense for a read-only template: the device selector driving
 * dynamic-value resolution, the Global/Customized scope chip, and copy.
 */
export const FrameViewerTopBar = ({
  deviceId,
  devices,
  editor,
  onDeviceSelect,
  onShowAllConditionalsChange,
  scopeLabel,
  showAllConditionals,
}: FrameViewerTopBarProps) => {
  const copyAction = useTiptapCopyAction({
    copySource: true,
    disabled: !editor,
    editor,
  });
  const selectedDevice = devices.find((device) => device.id === deviceId);
  const conditionalVisibilityLabel = `${
    showAllConditionals ? "Hide" : "Show"
  } false conditional values`;

  return (
    <div className="flex min-h-[49px] items-center justify-between border-0 border-b border-solid border-gray-200 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        {devices.length > 0 && (
          <CompactSelect
            value={deviceId ?? ""}
            onChange={onDeviceSelect}
            ariaLabel="Preview device"
            renderValue={() => selectedDevice?.name ?? "Select device"}
          >
            <ListSubheader>Preview values for</ListSubheader>
            {devices.map((device) => (
              <MenuItem key={device.id} value={device.id}>
                {device.name}
              </MenuItem>
            ))}
          </CompactSelect>
        )}
        {scopeLabel && (
          <DocumentScopeChip
            dataTestId="frame-viewer-scope-chip"
            label={scopeLabel}
            maxWidth={220}
          />
        )}
      </div>

      <div className="flex min-h-[32px] items-center justify-end gap-2">
        <GenericButton
          ariaLabel={conditionalVisibilityLabel}
          dataTestId="frame-conditional-visibility-button"
          onClick={() => onShowAllConditionalsChange(!showAllConditionals)}
          tooltipContent={conditionalVisibilityLabel}
        >
          {showAllConditionals ? (
            <VisibilityOutlined fontSize="small" />
          ) : (
            <VisibilityOffOutlined fontSize="small" />
          )}
        </GenericButton>
        {copyAction && (
          <GenericButton
            ariaLabel={
              copyAction.isCopied
                ? "Editor content copied"
                : "Copy editor content"
            }
            dataTestId="frame-copy-content-button"
            isDisabled={copyAction.isDisabled}
            onClick={() => copyAction.onClick()}
            tooltipContent={
              copyAction.isCopied ? "Copied" : "Copy editor content"
            }
          >
            {copyAction.isCopied ? (
              <CheckRounded color="success" fontSize="small" />
            ) : (
              <ContentCopyRounded fontSize="small" />
            )}
          </GenericButton>
        )}
      </div>
    </div>
  );
};
